import { DataTable } from '@/components/data-table/DataTable';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { PageHeader } from '@/components/feedback/PageHeader';
import { FillPage } from '@/components/layout/PageContainer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDateTime } from '@/lib/utils/date';
import type { ColumnDef } from '@tanstack/react-table';
import { AudioLines, Check, Eye, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  useAdminApprovedTtsFlowList,
  useAdminTtsFlowList,
  useBulkReviewTtsFlows,
  useReviewTtsFlow,
} from '../api';
import { TtsFlowPreviewModal } from '../components/TtsFlowPreviewModal';
import { templateLabel, type TtsFlowListRow } from '../types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

type ReviewAction = 'approve' | 'reject';
type ApprovalTab = 'pending' | 'approved';

export function TtsFlowApprovalPage() {
  const pendingQuery = useAdminTtsFlowList();
  const approvedQuery = useAdminApprovedTtsFlowList();

  const review = useReviewTtsFlow();
  const bulkReview = useBulkReviewTtsFlows();

  const [activeTab, setActiveTab] = useState<ApprovalTab>('pending');
  const [previewId, setPreviewId] = useState<TtsFlowListRow | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    row: TtsFlowListRow;
    action: ReviewAction;
  } | null>(null);
  const [pendingBulk, setPendingBulk] = useState<{
    ids: string[];
    clear: () => void;
    action: ReviewAction;
  } | null>(null);

  const pendingCount = pendingQuery.data?.length ?? 0;
  const approvedCount = approvedQuery.data?.length ?? 0;

  const sharedColumns = useMemo<ColumnDef<TtsFlowListRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'ttsFlowName',
        header: 'Flow',
        cell: ({ row }) => (
          <span className="flex items-center gap-2 font-medium text-foreground">
            <AudioLines className="h-3.5 w-3.5 text-muted-foreground" />
            {row.original.ttsFlowName}
          </span>
        ),
      },
      {
        accessorKey: 'userId',
        header: 'Owner ID',
        size: 120,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'templateId',
        header: 'Template',
        size: 160,
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal">
            {templateLabel(String(row.original.templateId))}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Submitted',
        size: 200,
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
    ],
    [],
  );

  const pendingColumns = useMemo<ColumnDef<TtsFlowListRow>[]>(
    () => [
      ...sharedColumns,
      {
        id: 'actions',
        header: 'Review',
        size: 130,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={`Preview ${row.original.ttsFlowName}`}
              title="Flow Preview"
              onClick={() => setPreviewId(row.original)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
              aria-label={`Approve ${row.original.ttsFlowName}`}
              onClick={() => setPendingAction({ row: row.original, action: 'approve' })}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              aria-label={`Reject ${row.original.ttsFlowName}`}
              onClick={() => setPendingAction({ row: row.original, action: 'reject' })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [sharedColumns],
  );

  const approvedColumns = useMemo<ColumnDef<TtsFlowListRow>[]>(
    () => [
      ...sharedColumns,
      {
        accessorKey: 'campaignIds',
        header: 'Campaigns',
        size: 180,
        cell: ({ row }) => {
          const ids = (row.original.campaignIds ?? '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);

          if (ids.length === 0) {
            return (
              <span className="text-xs text-muted-foreground" title="No associated campaigns">
                —
              </span>
            );
          }

          const MAX_VISIBLE = 3;
          const visible = ids.slice(0, MAX_VISIBLE);
          const overflow = ids.length - visible.length;

          return (
            <div className="flex flex-wrap items-center gap-1">
              {visible.map((id, idx) => (
                <Badge
                  key={`${id}-${idx}`}
                  variant="outline"
                  className="font-mono text-[11px] font-normal tabular-nums"
                >
                  {id}
                </Badge>
              ))}

              {overflow > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-5 items-center gap-0.5 rounded-md border border-dashed border-border bg-secondary px-1.5 text-[11px] font-normal text-muted-foreground transition-colors hover:bg-secondary/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Show all ${ids.length} campaign IDs`}
                    >
                      +{overflow} more
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-56 p-3" sideOffset={6}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground">
                        Campaigns ({ids.length})
                      </span>
                    </div>
                    <div className="flex max-h-40 flex-wrap gap-1 overflow-y-auto">
                      {ids.map((id, idx) => (
                        <Badge
                          key={`${id}-${idx}`}
                          variant="outline"
                          className="font-mono text-[11px] font-normal tabular-nums"
                        >
                          {id}
                        </Badge>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 70,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={`Preview ${row.original.ttsFlowName}`}
            title="Flow Preview"
            onClick={() => setPreviewId(row.original)}
          >
            <Eye className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [sharedColumns],
  );

  const confirmAction = () => {
    if (!pendingAction) return;
    const { row, action } = pendingAction;
    review.mutate(
      { id: row.id, action },
      {
        onSuccess: (res) => {
          toast.success(
            res?.message ??
              `"${row.ttsFlowName}" ${action === 'approve' ? 'approved' : 'rejected'}.`,
          );
          setPendingAction(null);
          if (previewId?.id === row.id) setPreviewId(null);
        },
        onError: () => toast.error(`Could not ${action} the TTS flow. Please try again.`),
      },
    );
  };

  const confirmBulkAction = () => {
    if (!pendingBulk) return;
    const { ids, clear, action } = pendingBulk;
    bulkReview.mutate(
      { ids, action },
      {
        onSuccess: (res) => {
          setPendingBulk(null);
          clear();
          toast.success(
            res?.message ??
              `${ids.length} TTS flow(s) ${action === 'approve' ? 'approved' : 'rejected'}.`,
          );
        },
        onError: () => toast.error(`Could not ${action} the selected TTS flows. Please try again.`),
      },
    );
  };

  const approving = pendingAction?.action === 'approve';
  const bulkApproving = pendingBulk?.action === 'approve';
  const bulkCount = pendingBulk?.ids.length ?? 0;

  return (
    <FillPage>
      <PageHeader
        title="TTS Flow Approval"
        description="Review user-submitted TTS flows before they go live. Preview the caller flow, then approve or reject."
      />

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as ApprovalTab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="self-start">
          <TabsTrigger value="pending" className="gap-1.5">
            Pending Approval
            <Badge variant="secondary" className="tabular-nums">
              {pendingCount}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-1.5">
            Approved
            <Badge variant="secondary" className="tabular-nums">
              {approvedCount}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">
          <DataTable<TtsFlowListRow, unknown>
            enableRowSelection={true}
            tableId="tts-flow:approval:pending:v1"
            columns={pendingColumns}
            data={pendingQuery.data ?? []}
            isLoading={pendingQuery.isLoading}
            searchPlaceholder="Search pending flows…"
            emptyTitle="Nothing awaiting approval"
            emptyDescription="New TTS flows submitted by users will appear here for review."
            getRowId={(row) => String(row.id)}
            initialColumnPinning={{ right: ['actions'] }}
            pageSize={10}
            renderSelectionActions={(ids, clear) => (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => setPendingBulk({ ids, clear, action: 'approve' })}
                  disabled={ids.length === 0 || bulkReview.isPending}
                >
                  <Check className="h-4 w-4" />
                  Approve {ids.length}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPendingBulk({ ids, clear, action: 'reject' })}
                  disabled={ids.length === 0 || bulkReview.isPending}
                >
                  <X className="h-4 w-4" />
                  Reject {ids.length}
                </Button>
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="approved" className="mt-4 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">
          <DataTable<TtsFlowListRow, unknown>
            enableRowSelection={false}
            tableId="tts-flow:approval:approved:v1"
            columns={approvedColumns}
            data={approvedQuery.data ?? []}
            isLoading={approvedQuery.isLoading}
            searchPlaceholder="Search approved flows…"
            emptyTitle="No approved flows yet"
            emptyDescription="Flows you approve from the Pending tab will show up here."
            getRowId={(row) => String(row.id)}
            initialColumnPinning={{ right: ['actions'] }}
            pageSize={10}
          />
        </TabsContent>
      </Tabs>

      {previewId && (
        <TtsFlowPreviewModal
          key={previewId.id}
          rowId={previewId.id}
          flowName={previewId.ttsFlowName}
          templateId={String(previewId.templateId)}
          createdAt={previewId.createdAt}
          ownerUserId={previewId.userId}
          onClose={() => setPreviewId(null)}
        />
      )}

      {/* Single-row review */}
      <ConfirmDialog
        open={pendingAction !== null}
        onOpenChange={(o) => !o && setPendingAction(null)}
        title={approving ? 'Approve TTS flow?' : 'Reject TTS flow?'}
        description={
          <>
            TTS flow{' '}
            <span className="font-medium text-foreground">{pendingAction?.row.ttsFlowName}</span>{' '}
            will be{' '}
            {approving
              ? 'approved and become available to the user.'
              : 'rejected and returned to the user as not approved.'}
          </>
        }
        confirmLabel={approving ? 'Approve' : 'Reject'}
        tone={approving ? 'default' : 'destructive'}
        loading={review.isPending}
        onConfirm={confirmAction}
      />

      {/* Bulk review */}
      <ConfirmDialog
        open={pendingBulk !== null}
        onOpenChange={(o) => !o && setPendingBulk(null)}
        title={
          bulkApproving ? `Approve ${bulkCount} TTS flow(s)?` : `Reject ${bulkCount} TTS flow(s)?`
        }
        description={
          <>
            <span className="font-medium text-foreground">{bulkCount}</span> TTS flow(s) will be{' '}
            {bulkApproving
              ? 'approved and become available to their owners.'
              : 'rejected and returned to their owners as not approved.'}
          </>
        }
        confirmLabel={bulkApproving ? 'Approve' : 'Reject'}
        tone={bulkApproving ? 'default' : 'destructive'}
        loading={bulkReview.isPending}
        onConfirm={confirmBulkAction}
      />
    </FillPage>
  );
}
